/**
 * Whether a route may be opened, once the session can actually answer for the user.
 *
 * Roles are not known while the session is still initializing, which is exactly the state a cold
 * deep link arrives in — a bookmark, a pasted URL, a reload. Asking then would answer "no roles at
 * all" and turn a warehouse manager away from their own page, so the question waits for the
 * session to settle rather than guessing at it.
 *
 * Only an authenticated user is ever turned away. Somebody who is simply not logged in is let
 * through to meet the normal login flow, because sending them to a 404 would lose the page they
 * were trying to reach in the first place.
 *
 * This is a second line rather than the only one: the server refuses the data regardless, so what
 * this prevents is a page that loads and then cannot fill itself — a dead end that reads like a
 * broken app rather than like a boundary.
 */
export function guardRole (to, session, next) {
  const required = to && to.meta ? to.meta.requires_role : null

  if (!required) {
    next()
    return
  }

  const decide = () => {
    if (session.state === 'authenticated' && !session.hasUserRole(required)) {
      next({name: '404'})
      return
    }

    next()
  }

  if (session.state === 'authenticated' || session.state === 'ready') {
    decide()
    return
  }

  const unwatch = session.$subscribe((mutation, state) => {
    if (state.state === 'authenticated' || state.state === 'ready') {
      unwatch()
      decide()
    }
  })
}

export default guardRole
